import { Box, Drawer, DrawerContent, DrawerOverlay, Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIsMobile } from '../../helpers/useIsMobile';
import { logger } from '../../helpers/logger';
import { TrackerEventService } from '../../containers/prescription/trackerEventService';
import { getWhatsappPbmBenefit, Medicine } from '../../interfaces/medicine';
import { MedicineForEdition } from '../../interfaces/medicineEdition';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import PbmCancelModal from '../pbm/PbmCancelModal';
import WhatsappPbmConsentStep from './WhatsappPbmConsentStep';
import WhatsappPbmFeedbackStep from './WhatsappPbmFeedbackStep';
import { WHATSAPP_PBM_PROGRAM_TYPE, WhatsappPbmStep } from './constants';
import { APP_VIEWPORT_HEIGHT } from '../../helpers/embeddedViewport';

interface WhatsappPbmSidebarProps {
  isOpen: boolean;
  /** Chamado em qualquer fechamento: sucesso, erro ou cancelamento confirmado. */
  onClose: () => void;
  /** Chamado quando o consentimento foi salvo na prescrição. */
  onConsentSaved?: () => void;
  medicineData?: Medicine | MedicineForEdition;
  prescription?: {
    idPrescricao: number;
    CodigoValidacao?: string;
    Paciente?: { idPaciente: number };
    Prescritor?: { idPrescritor: number };
    idPrescritor?: number;
  };
  partnerId?: number;
  initialStep?: WhatsappPbmStep;
}

/**
 * Sidebar do PBM via WhatsApp: o médico registra o consentimento do paciente, que é salvo na
 * prescrição (`PBMConsentimentoDado`). O backend envia a mensagem do benefício na emissão.
 */
const WhatsappPbmSidebar: React.FC<WhatsappPbmSidebarProps> = ({
  isOpen,
  onClose,
  onConsentSaved,
  medicineData,
  prescription,
  partnerId,
  initialStep,
}) => {
  const isMobile = useIsMobile();
  const [step, setStep] = useState<WhatsappPbmStep>(initialStep ?? 'consent');
  const [consentGiven, setConsentGiven] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const previousIsOpenRef = useRef<boolean>(isOpen);

  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;
    previousIsOpenRef.current = isOpen;

    if (isOpen && !wasOpen) {
      setStep(initialStep ?? 'consent');
      setConsentGiven(false);
      setIsLoading(false);
      setShowCancelModal(false);
    }
  }, [isOpen, initialStep]);

  const handleEventEmission = useCallback(
    async (eventName: string, extraParams: Record<string, unknown> = {}) => {
      if (!prescription?.idPrescricao) return;

      try {
        await TrackerEventService.track(prescription.idPrescricao, eventName, {
          idPrescricao: prescription.idPrescricao,
          codigoValidacao: prescription.CodigoValidacao,
          idParceiro: partnerId,
          idMedico: prescription.Prescritor?.idPrescritor ?? prescription.idPrescritor,
          idPaciente: prescription.Paciente?.idPaciente,
          idMedicamento: medicineData?.IdMedicamento,
          listaEans:
            medicineData?.EANs ?? (medicineData && 'ListaEANS' in medicineData ? medicineData.ListaEANS : []) ?? [],
          nomeDoPrograma: getWhatsappPbmBenefit(medicineData?.Beneficios)?.Nome,
          isMobile,
          tipoDePrograma: WHATSAPP_PBM_PROGRAM_TYPE,
          ambiente: process.env.REACT_APP_ENVIRONMENT || 'staging',
          ...extraParams,
        });
      } catch (error: unknown) {
        logger.error('[WhatsApp PBM - EventEmission] Erro ao emitir evento de analytics:', { eventName, error });
      }
    },
    [prescription, partnerId, medicineData, isMobile]
  );

  const handleConsentConfirm = async () => {
    if (!consentGiven || !medicineData) return;

    handleEventEmission('backoffice_campaign_pbm_consent_click', {
      consentPaciente: consentGiven,
      flow: 'consentimento',
    });

    setIsLoading(true);
    try {
      await usePrescriptionStore.getState().saveMedicineWhatsappPbmConsent(medicineData);
      setStep('success');
      onConsentSaved?.();
    } catch (error: unknown) {
      logger.error('[WhatsApp PBM - Consent] Erro ao salvar consentimento na prescrição:', {
        error,
        idPrescricao: prescription?.idPrescricao,
        idMedicamento: medicineData.IdMedicamento,
      });
      setStep('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setConsentGiven(false);
    setStep('consent');
  };

  const handleClose = () => {
    if (step !== 'consent') {
      onClose();
      return;
    }
    setShowCancelModal(true);
    handleEventEmission('backoffice_campaign_pbm_cancel_consent_view', { flow: 'consentimento' });
  };

  const handleCancelConfirm = () => {
    setShowCancelModal(false);
    onClose();
    handleEventEmission('backoffice_campaign_pbm_cancela_precadastro_click', {
      flow: 'consentimento',
      continouPreCadastro: false,
    });
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
    handleEventEmission('backoffice_campaign_pbm_cancela_precadastro_click', {
      flow: 'consentimento',
      continouPreCadastro: true,
    });
  };

  const renderContent = () => {
    if (step === 'success' || step === 'error') {
      return (
        <WhatsappPbmFeedbackStep
          key={step}
          status={step}
          onClose={handleClose}
          onRetry={handleRetry}
          onEventEmission={handleEventEmission}
        />
      );
    }

    return (
      <WhatsappPbmConsentStep
        programName={getWhatsappPbmBenefit(medicineData?.Beneficios)?.Nome}
        consentGiven={consentGiven}
        onConsentChange={setConsentGiven}
        isLoading={isLoading}
        onConfirm={handleConsentConfirm}
        onClose={handleClose}
      />
    );
  };

  return (
    <>
      <PbmCancelModal isOpen={showCancelModal} onClose={handleCancelModalClose} onConfirm={handleCancelConfirm} />

      {isMobile ? (
        <Modal isOpen={isOpen} onClose={handleClose} size="full" isCentered={false}>
          <ModalOverlay />
          <ModalContent
            maxW="100vw"
            maxH={APP_VIEWPORT_HEIGHT}
            m={0}
            borderRadius={0}
            height={APP_VIEWPORT_HEIGHT}
            display="flex"
            flexDirection="column"
          >
            <Box height={APP_VIEWPORT_HEIGHT} display="flex" flexDirection="column" overflow="hidden">
              {renderContent()}
            </Box>
          </ModalContent>
        </Modal>
      ) : (
        <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
          <DrawerOverlay />
          <DrawerContent>{renderContent()}</DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export default WhatsappPbmSidebar;
